import html2canvas from "html2canvas";
import store, {AppDispatch, RootState} from "../../store";
import {changeScreenShotNodeId} from "../../reducers/app";
import {ServiceInstance} from "../ServiceContainer";
import YoutubeBg from "./img/youtube-bg.svg";
import YoutubeIcon from "./img/youtube-icon.svg";
import ChannelIcon from "./img/channel-icon.svg";
import DotsVerticalIcon from "./img/dots-vertical.svg";
import GeneralBg from "./img/general-bg.svg";
import GeneralIcon from "./img/general-icon.svg";
import { RequestPageType } from "../../types/RequestPageType";
import { fixGradientTextForScreenshot } from "./utils/fixGradientTextForScreenshot";
import { fixBorderImageForScreenshot } from "./utils/fixBorderImageForScreenshot";
import { fixObjectFitForScreenshot } from "./utils/fixObjectFitForScreenshot";
import { pageService } from "../Page/usePageService";
import { collectFontUsage } from "../../fonts/FontUsageCollector";
import { loadFont } from "../../fonts/FontLoader";
import { filterImageVariantsForScreenshot } from "./utils/filterImageVariantsForScreenshot";

const EDIT_CLASSES = ['solid-blue', 'solid-light-blue', 'dashed-blue', 'dashed-grey', 'border-invalid-action'];
const SCREENSHOT_ASSET_TIMEOUT = 3000;

const waitForScreenshotLayout = async (element: HTMLElement): Promise<void> => {
    const ownerDocument = element.ownerDocument;
    await ownerDocument.fonts?.ready;

    const images = Array.from(element.querySelectorAll('img'));
    await Promise.all(images.map((image) => {
        if (image.complete) return Promise.resolve();
        return new Promise<void>((resolve) => {
            const timeout = window.setTimeout(resolve, SCREENSHOT_ASSET_TIMEOUT);
            const finish = () => {
                window.clearTimeout(timeout);
                resolve();
            };

            image.addEventListener('load', finish, {once: true});
            image.addEventListener('error', finish, {once: true});
        });
    }));

    await new Promise<void>((resolve) => requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
    }));
};

export interface ScreenShotProcessor {
    onScreenShotReady(result: string): Promise<string>

    getNodeId(): string

    onScreenShotAction(): Promise<string>
}

export class ScreenShotService implements ServiceInstance {
    screenShotProcessor: ScreenShotProcessor;
    private dispatch: AppDispatch;
    private screenShotQueue: Promise<string> = Promise.resolve('');
    private resolveScreenShot?: (result: string) => void;
    private rejectScreenShot?: (reason?: unknown) => void;
    private isProcessing = false;

    public constructor(dispatch: AppDispatch) {
        this.dispatch = dispatch
    }

    private replaceIframesWithPlaceholders(newDiv: HTMLElement, isElementMainNode: boolean): void {
        const isHasParentPage = !!pageService.getPage()?.parentTemplate;
        const iframes = newDiv.querySelectorAll("iframe");
        iframes.forEach((iframe) => {
            const iframeComputedStyle = getComputedStyle(iframe);
            const wrapper = document.createElement('div');
            if((isElementMainNode || !isHasParentPage) && iframe.src.includes("youtube.com")) {
                wrapper.innerHTML = `
                <div style="background-image: url('${YoutubeBg}'); background-size: cover; background-position: center; background-repeat: no-repeat; position: relative; padding: 25px; width: ${iframeComputedStyle.width}; height: ${iframeComputedStyle.height};">
                    <img src='${YoutubeIcon}' style="width: 70px; position: absolute; top: 50%; right: 50%; transform: translate(50%, -50%);" />
                    <div style="display: flex; justify-content: space-between; align-items: center">
                        <div style="display: flex; align-items: center; gap: 16px;">
                            <img src='${ChannelIcon}' style="width: 39px" />
                            <div style="color: white">Video Channel</div>
                        </div>
                        <img src='${DotsVerticalIcon}' style="width: 25px" />
                    </div>
                </div>
                `;
            } else {
                wrapper.innerHTML = `
                <div style="background-image: url('${GeneralBg}'); background-size: cover; background-position: center; background-repeat: no-repeat; position: relative; padding: 22px; width: ${iframeComputedStyle.width}; height: ${iframeComputedStyle.height};">
                    <img src='${GeneralIcon}' style="width: 70px; position: absolute; top: 50%; right: 50%; transform: translate(50%, -50%);" />
                </div>
                `;
            }

            iframe.replaceWith(wrapper);
        });
    }

    runScreenShot(screenShotProcessor: ScreenShotProcessor): void {
        this.runScreenShotAndWait(screenShotProcessor)
            .catch(error => console.error('Screenshot creation error: ', error));
    }

    runScreenShotAndWait(screenShotProcessor: ScreenShotProcessor): Promise<string> {
        const screenShotTask = this.screenShotQueue
            .catch(() => '')
            .then(() => new Promise<string>((resolve, reject) => {
                this.screenShotProcessor = screenShotProcessor;
                this.resolveScreenShot = resolve;
                this.rejectScreenShot = reject;
                this.dispatch(changeScreenShotNodeId({id: screenShotProcessor.getNodeId(), key: Date.now()}));
            }));

        this.screenShotQueue = screenShotTask;
        return screenShotTask;
    }

    onScreenShotAction(): Promise<string> {
        return this.screenShotProcessor.onScreenShotAction()
    }

    onScreenShotReady(result: string): Promise<string> {
        return this.screenShotProcessor.onScreenShotReady(result);
    }

    async processScreenShot(): Promise<void> {
        if (this.isProcessing) return;

        this.isProcessing = true;
        try {
            const result = await this.onScreenShotAction();
            if (!result) throw new Error('Screenshot creation failed');

            const uploadedImage = await this.onScreenShotReady(result);
            this.resolveScreenShot?.(uploadedImage);
        } catch (error) {
            this.rejectScreenShot?.(error);
        } finally {
            this.isProcessing = false;
            this.resolveScreenShot = undefined;
            this.rejectScreenShot = undefined;
            this.dispatch(changeScreenShotNodeId(undefined));
        }
    }
    static  takeMobileScreenShot(element: HTMLElement) {
        return ScreenShotService.takeScreenshotOfElement(element, false, false, true)
    }

    static takeTemplateScreenShot(element: HTMLElement) {
        const state: RootState = store.getState();
        const pageType = state.appStorage.type;
        return ScreenShotService.takeScreenshotOfElement(element, pageType === RequestPageType.CommonPage ? true : false, true)
    }

    static takePCScreenShot(element: HTMLElement) {
        return ScreenShotService.takeScreenshotOfElement(element, true, false, true)
    }

    static takeScreenshotOfElement = async (element: HTMLElement, isPcScreen?: boolean, isTemplateScreenShot?: boolean, useDirectElement: boolean = false) => {
        const iframe = document.getElementById("mobileView") as HTMLIFrameElement
        const iframeDocument = iframe?.contentDocument || iframe?.contentWindow?.document;
        const mainNode: Element = useDirectElement
            ? element
            : ((iframeDocument ? iframeDocument.querySelector(`#${element.id}`) : null) ?? document.querySelector(`#${element.id}`)) as Element;
        
        const maxWidth = 1000;
        const maxHeight = 825;
        const elementWidth = {desktop: isTemplateScreenShot ? 1440 : 1920, mobile: 400}
        const elementHeight = {mobile: 750}
        const width = isPcScreen ? maxWidth : elementWidth.mobile;
        const height = isPcScreen ? Math.min(element.offsetHeight, maxHeight) : elementHeight.mobile;
        const scale = width / (isPcScreen ? elementWidth.desktop : elementWidth.mobile);

        const computedStyle = getComputedStyle(mainNode);
        const { background, backgroundImage, backgroundSize, backgroundPosition } = computedStyle;

        const sourceDocument = mainNode.ownerDocument;
        const sourceWindow = sourceDocument.defaultView;
        const fonts = sourceWindow ? collectFontUsage(mainNode, sourceWindow) : [];
        if (fonts.length) {
            const targetDocuments = Array.from(new Set([sourceDocument, document]));
            await Promise.all(
                targetDocuments.flatMap((targetDocument) =>
                    fonts.map((font) =>
                        loadFont(font.family, font.weight, font.style, targetDocument),
                    ),
                ),
            );
        }

        const newDiv = document.createElement('div');
        newDiv.style.position = "relative";


        const absoluteDiv = document.createElement('div');
        absoluteDiv.style.position = "absolute"
        absoluteDiv.style.left = "-999999px"
        absoluteDiv.style.top = "-999999px"
        absoluteDiv.style.width = `${isPcScreen ? elementWidth.desktop : elementWidth.mobile}px`;
        absoluteDiv.style.transform = `scale(${scale})`;
        absoluteDiv.style.transformOrigin = "top left";
        
        if (background) absoluteDiv.style.background = background;
        if (backgroundImage) absoluteDiv.style.backgroundImage = backgroundImage;
        if (backgroundSize) absoluteDiv.style.backgroundSize = backgroundSize;
        if (backgroundPosition) absoluteDiv.style.backgroundPosition = backgroundPosition;
        
        document.body.appendChild(newDiv);
        newDiv.appendChild(absoluteDiv);
        
        const clonedElement = mainNode.cloneNode(true) as HTMLElement;
        clonedElement.style.width = `${isPcScreen ? elementWidth.desktop : elementWidth.mobile}px`;
        clonedElement.style.height = "auto";
        clonedElement.style.transform = "none";
        clonedElement.style.position = "relative";
        [clonedElement, ...Array.from(clonedElement.querySelectorAll('*'))].forEach(el => {
            EDIT_CLASSES.forEach(cls => (el as HTMLElement).classList.remove(cls));
        });
        filterImageVariantsForScreenshot(clonedElement, Boolean(isPcScreen));
        absoluteDiv.appendChild(clonedElement);
        clonedElement.querySelectorAll<HTMLElement>("header.sticky-basic, .sticky").forEach((el) => {
            if (getComputedStyle(el).position !== "sticky") return;
            el.style.position = "relative";
            el.style.top = "auto";
        });

        await waitForScreenshotLayout(clonedElement);
        
        const scaledHeight = clonedElement.scrollHeight * scale;
        const finalHeight = isTemplateScreenShot ? scaledHeight : height;
        absoluteDiv.style.height = `${finalHeight}px`;

        this.prototype.replaceIframesWithPlaceholders(absoluteDiv, mainNode.id === element.id);
        await fixBorderImageForScreenshot(absoluteDiv);
        await fixObjectFitForScreenshot(absoluteDiv);

        try {
            const canvas = await html2canvas(absoluteDiv, {
                useCORS: true,
                width: width,
                height: finalHeight,
                windowWidth: isPcScreen ? elementWidth.desktop : elementWidth.mobile,
                windowHeight: finalHeight,
                scrollX: 0,
                scrollY: 0,
                logging: false,
                onclone: async (clonedDoc) => {
                    await Promise.all(
                        fonts.map((font) => loadFont(font.family, font.weight, font.style, clonedDoc)),
                    );
                    await clonedDoc.fonts?.ready;
                    fixGradientTextForScreenshot(clonedDoc);
                }
            });

            return canvas.toDataURL("image/jpeg", 0.6);
        } catch (error) {
            console.error("Screenshot creation error: ", error);
            return null;
        } finally {
            newDiv.remove();
        }
    };
}
